import type { PermissionGroupId } from '@strength/domain';
import { PERMISSION_GROUPS } from '@strength/domain';

/**
 * UI-only metadata for the User Permissions drawer: which top-level category
 * (a plain heading, not its own card) each group is displayed under. Action
 * labels/descriptions and group titles live on `PERMISSION_GROUPS` itself —
 * this file only holds what's left: layout.
 */
export type PermissionCategory =
  | 'library'
  | 'plan'
  | 'actuals'
  | 'notes'
  | 'teams'
  | 'tablet';

export const GROUP_CATEGORIES: Record<PermissionGroupId, PermissionCategory> = {
  'library.exercises': 'library',
  'library.schemes': 'library',
  'library.templates': 'library',
  'library.tags': 'library',
  'library.exerciseMaxConversions': 'library',
  'plan.tracks': 'plan',
  'plan.blocks': 'plan',
  'plan.phases': 'plan',
  actuals: 'actuals',
  notes: 'notes',
  'teams.profiles': 'teams',
  'teams.activityLog': 'teams',
  'teams.benchmarks': 'teams',
  'teams.settings': 'teams',
  tablet: 'tablet',
};

/**
 * Category display order, derived from `PERMISSION_GROUPS` so adding or
 * reordering a group there is reflected here automatically — no manual list
 * to keep in sync.
 */
export const CATEGORY_ORDER: PermissionCategory[] = [
  ...new Set(PERMISSION_GROUPS.map((group) => GROUP_CATEGORIES[group.id])),
] as PermissionCategory[];

export const CATEGORY_TITLE_KEYS: Record<PermissionCategory, string> = {
  library: 'library',
  plan: 'plan',
  actuals: 'actuals',
  notes: 'notes',
  teams: 'teams',
  tablet: 'tabletMode',
};

export const GROUPS_BY_CATEGORY: Record<
  PermissionCategory,
  (typeof PERMISSION_GROUPS)[number][]
> = CATEGORY_ORDER.reduce(
  (acc, category) => {
    acc[category] = PERMISSION_GROUPS.filter(
      (group) => GROUP_CATEGORIES[group.id] === category,
    );
    return acc;
  },
  {} as Record<PermissionCategory, (typeof PERMISSION_GROUPS)[number][]>,
);
